import { useState, useMemo, useEffect } from "react";
import { Plus, Minus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;

interface GarcomProductGridProps {
  products: Product[];
  getCartQuantity: (productId: string) => number;
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  openNoteDialog: (product: Product) => void;
  formatCurrency: (value: number) => string;
}

const ITEMS_PER_PAGE = 12; // 2 columns x 6 rows for mobile

export function GarcomProductGrid({
  products,
  getCartQuantity,
  addToCart,
  removeFromCart,
  openNoteDialog,
  formatCurrency,
}: GarcomProductGridProps) {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when products change (e.g., category filter)
  useEffect(() => {
    setCurrentPage(1);
  }, [products.length]);

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return products.slice(start, start + ITEMS_PER_PAGE);
  }, [products, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 grid grid-cols-2 gap-1.5 content-start">
        {paginatedProducts.map((product) => {
          const qty = getCartQuantity(product.id);
          return (
            <Card
              key={product.id}
              className={cn(
                "p-2 relative overflow-hidden transition-all",
                qty > 0 && "ring-2 ring-primary"
              )}
            >
              <button
                className="w-full text-left"
                onClick={() => addToCart(product)}
              >
                <p className="font-medium text-xs line-clamp-2 mb-0.5">
                  {product.name}
                </p>
                <p className="text-xs font-bold text-primary">
                  {formatCurrency(Number(product.price))}
                </p>
              </button>

              {qty > 0 && (
                <div className="flex items-center justify-between mt-2 pt-2 border-t">
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-7 w-7"
                      onClick={() => removeFromCart(product.id)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-6 text-center font-bold text-sm">
                      {qty}
                    </span>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-7 w-7"
                      onClick={() => addToCart(product)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs h-7 px-2"
                    onClick={() => openNoteDialog(product)}
                  >
                    Obs
                  </Button>
                </div>
              )}

              {qty > 0 && (
                <Badge className="absolute top-1 right-1 h-5 w-5 p-0 flex items-center justify-center">
                  {qty}
                </Badge>
              )}
            </Card>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-2 border-t mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="h-8 px-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="h-8 px-2"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
